<script setup lang="ts">
import * as R from 'ramda'
import { computed, reactive } from 'vue'

import { Icon, Modal, runLoader, Separator } from '@collector/shared-ui'
import {
  CommonResultId,
  StatusType,
  YesNoEnum,
} from '@collector/sportsapi-client-legacy'
import { sportsApiClient } from '@desktop/globalState/sportsApiClient'
import Result from '@desktop/views/Relation/components/RelationView/Summary/Result.vue'
import Summary from '@desktop/views/Relation/components/RelationView/Summary/Summary.vue'
import {
  useEvent,
  useParticipantResultsMaps,
  useParticipants,
  useStatistics,
} from '@desktop/views/Relation/composables'
import SummaryParticipantsImportantStats from '@desktop/views/Relation/Sports/components/RelationView/SummaryParticipantsImportantStats.vue'
import { injectVerifyResultSummaryConfig } from '@desktop/views/Relation/Sports/provide-inject'

import AdditionalOption from './AdditionalOption.vue'
import Table from './Table/Table.vue'

type Emits = { close: [] }
const emit = defineEmits<Emits>()

// composables
const { homeParticipant, awayParticipant } = useParticipants()
const { homeParticipantResultsMap, awayParticipantResultsMap } =
  useParticipantResultsMaps()

const { statsVisible } = useStatistics()

const { event } = useEvent()

// inject
const verifyResultSummaryConfig = injectVerifyResultSummaryConfig()

// state
interface State {
  winner: [number, number]
  progress: [number, number]
}

const state = reactive<State>(R.clone(getDefaultState()))

// computed
const isDrawPossible = computed(
  () => !!verifyResultSummaryConfig?.isDrawPossible,
)

const hasEventStatusFinished = computed(
  () => event.value.status_type === StatusType.Finished,
)

const verificationDisabled = computed(
  () =>
    !hasEventStatusFinished.value ||
    state.winner.some((winner) => winner === -1) ||
    state.progress.some((progress) => progress === -1),
)

const options = computed(() => {
  const homeName = homeParticipant.value?.short_name ?? ''
  const awayName = awayParticipant.value?.short_name ?? ''

  if (isDrawPossible.value) {
    return {
      winnerOptions: [
        homeName,
        verifyResultSummaryConfig?.winnerOptions?.drawOptionLabel || 'Draw',
        awayName,
      ],
      progressOptions: [homeName, 'N/A', awayName],
    }
  }
  return {
    winnerOptions: [homeName, awayName],
    progressOptions: [homeName, 'N/A', awayName],
  }
})

// methods
function getDefaultState(): State {
  if (event.value.verified_result === YesNoEnum.No) {
    return {
      winner: [-1, -1],
      progress: [-1, -1],
    }
  }

  return {
    winner: [
      Number(homeParticipantResultsMap.value[CommonResultId.Winner].value),
      Number(awayParticipantResultsMap.value[CommonResultId.Winner].value),
    ],
    progress: [
      Number(homeParticipantResultsMap.value[CommonResultId.Progress].value),
      Number(awayParticipantResultsMap.value[CommonResultId.Progress].value),
    ],
  }
}

async function verifyResult(): Promise<void> {
  const home = homeParticipant.value
  const away = awayParticipant.value
  if (!home || !away) return

  await runLoader(
    Promise.all([
      // verify event
      sportsApiClient.Events.patch(event.value.id, {
        event: { is_result_verified: YesNoEnum.Yes },
      }),

      // update winner and progress
      sportsApiClient.Events.Participants.patch(event.value.id, [
        {
          id: home.id,
          stats: [],
          results: [
            {
              id: CommonResultId.Winner,
              value: state.winner[0],
            },
            {
              id: CommonResultId.Progress,
              value: state.progress[0],
            },
          ],
        },
        {
          id: away.id,
          stats: [],
          results: [
            {
              id: CommonResultId.Winner,
              value: state.winner[1],
            },
            {
              id: CommonResultId.Progress,
              value: state.progress[1],
            },
          ],
        },
      ]),
    ]),
  )
  statsVisible.value = true
}
</script>

<template>
  <Modal
    title="Event verification"
    titleClass="mb-1"
    :confirmButton="{
      text: 'Verify result',
      confirm: verifyResult,
      disabled: verificationDisabled,
      tooltip: hasEventStatusFinished
        ? undefined
        : `select Event Status 'Finished' to Verify result`,
    }"
    @close="emit('close')"
  >
    <Summary>
      <template #result>
        <div class="text-neutral-dark-3">
          <Result />
        </div>
      </template>
      <template #bottom>
        <SummaryParticipantsImportantStats
          v-if="
            verifyResultSummaryConfig &&
            verifyResultSummaryConfig.showParticipantStats
          "
        />
      </template>
    </Summary>
    <div class="mx-4">
      <Table />
    </div>

    <Separator class="mx-1.5 my-2" />

    <AdditionalOption
      v-model="state.winner"
      :options="options.winnerOptions"
      title="Winner"
      :isDrawPossible
    >
      <Icon icon="trophy" />
    </AdditionalOption>

    <AdditionalOption
      v-model="state.progress"
      class="mt-2"
      :options="options.progressOptions"
      title="Progress"
    >
      <Icon
        class="mx-1"
        icon="arrow-turn-up"
      />
    </AdditionalOption>
  </Modal>
</template>
